/**
 * A story, the way a phone tells one: full bleed, one beat per screen, swiped
 * through with a thumb.
 *
 * This replaces what the first meeting and Wrapped used to be — a small card in
 * the middle of the screen with a „Toliau" button under it, eleven times in a
 * row. That shape is right for a confirmation and wrong for a performance: the
 * button is the only thing that moves, the card never changes size, and by the
 * third one you are pressing to make it stop rather than reading.
 *
 * Three ways forward, because a phone is held in three ways: swipe (the point),
 * tap the right side of the screen (the story convention everyone already
 * knows), and arrow keys for a desktop. The right edge of the screen goes
 * forward, the left third goes back — same as every stories UI since 2016. A
 * single affordance sits at the bottom for the case where none of that is
 * obvious: a breathing chevron, which on the last slide becomes the way out.
 *
 * `hold` is how a slide refuses to be left — the meeting's question hub uses it
 * to keep the unskippable contract without a disabled button.
 */

import { cn } from '@gramas/ui'
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { claimPresence, releasePresence } from '../glitime/presenceRegistry.js'
import { closeGate, openGate } from '../shell/toastStore.js'
import { playCue, startBed, stopBed } from '../sound/engine.js'
import { Aurora } from './Aurora.js'

/** Past this, a press is a drag and the slide follows the finger. */
const DRAG_START_PX = 9
/** Far enough that a lazy thumb-shift on a tap is never a page turn. */
const COMMIT_PX = 58
/** A flick: short, but unmistakably thrown. */
const FLICK_PX = 22
const FLICK_SPEED = 0.45
/** Under this much travel the gesture was a tap, wherever it ended. */
const TAP_PX = 8
/** Left of this fraction of the width, a tap goes back instead of forward. */
const BACK_ZONE = 0.3

export interface StageProps {
  /** Names the whole performance for a screen reader. */
  label: string
  count: number
  index: number
  onIndex: (next: number) => void
  /** Called when the last slide is left forwards. */
  onFinish: () => void
  /** What the last slide's affordance says — „Pradedam", „Uždaryti". */
  finishLabel: string
  /**
   * Set to refuse to advance, with the reason shown where the chevron was.
   * Null means the way forward is open.
   */
  hold?: string | null
  /** A corner ✕. Omitted by anything that must be sat through. */
  onClose?: () => void
  /**
   * Who is performing, in the top band. Null until she has said it herself —
   * the meeting withholds it for one slide so the name arrives with the beat
   * that is about the name.
   */
  crest?: string | null
  /** The band's right-hand caption: the week's dates, the name of the arc. */
  note?: string
  /** The light in the room for this slide — one colour per beat. */
  hue: string
  /**
   * Give the room a sound: a low bed under the whole thing, a note as each
   * slide arrives, and a resolve on the way out.
   *
   * Opt-in rather than automatic, because the first meeting is finished work
   * and this release is not touching it. Wrapped and the mini-ceremonies pass
   * it; the meeting does not, and behaves exactly as it always has.
   */
  cues?: boolean
  children: ReactNode
}

export function Stage({
  label,
  count,
  index,
  onIndex,
  onFinish,
  finishLabel,
  hold = null,
  onClose,
  crest = null,
  note,
  hue,
  cues,
  children,
}: StageProps) {
  const [drag, setDrag] = useState(0)
  const [dir, setDir] = useState<'fwd' | 'back'>('fwd')
  const surface = useRef<HTMLDivElement | null>(null)
  const gesture = useRef<{
    id: number
    x: number
    y: number
    lastX: number
    at: number
    speed: number
    axis: 'undecided' | 'x' | 'y'
  } | null>(null)

  const isLast = index >= count - 1
  // Materialised rather than mapped from a length, so each segment is keyed by
  // the position it *is* rather than by where it happened to land.
  const segments = Array.from({ length: count }, (_, i) => i)

  // Keyboard reaches the sequence only if something inside it is focused, and
  // on a phone there is nothing to focus. Taking focus on mount also takes it
  // away from whatever was behind this, which is the correct thing for a
  // full-screen takeover.
  useEffect(() => {
    surface.current?.focus({ preventScroll: true })
  }, [])

  /**
   * Hold the toasts while she performs.
   *
   * Not a z-index: toasts sit above sheets on purpose, because most of this
   * app's undo offers are raised from inside one, and an undo painted
   * underneath the sheet that raised it loses data. A performance is the one
   * layer that has to win, so instead of demoting the toast host, the store is
   * asked to wait. Errors and undo offers still come through — those are worth
   * interrupting a ceremony for; a confirmation that a yoghurt was logged is
   * not.
   *
   * Also claims top rank in the presence registry, so every other mark in the
   * app — the greeting strip, a spinner, whatever — goes dark for as long as
   * she is performing here, even though this performance draws its own mark
   * in `parts.tsx` rather than a `GlitimeMark`.
   */
  useEffect(() => {
    closeGate()
    const claim = claimPresence('stage')
    return () => {
      openGate()
      releasePresence(claim)
    }
  }, [])

  /**
   * The room's own sound, where the performance asked for one. Opt-in rather
   * than automatic so the first meeting — which is finished, and which nobody
   * is touching — behaves exactly as it did.
   */
  useEffect(() => {
    if (cues !== true) return
    startBed()
    return () => stopBed()
  }, [cues])

  // One note per slide, and a different one when the whole thing resolves.
  // The ref starts at the slide the stage opened on, so the first beat is
  // silent — it already has the bed arriving underneath it.
  const soundedSlide = useRef(index)
  useEffect(() => {
    if (cues !== true || soundedSlide.current === index) return
    soundedSlide.current = index
    playCue('slide')
  }, [cues, index])

  /** The resolve. Played on the way out, over the bed fading under it. */
  const finish = () => {
    if (cues === true) playCue('signoff')
    onFinish()
  }

  const go = (next: number) => {
    if (next === index) return
    if (next < 0) return
    if (next >= count) {
      if (hold != null) return
      finish()
      return
    }
    if (next > index && hold != null) return
    setDir(next > index ? 'fwd' : 'back')
    onIndex(next)
  }

  const forward = () => go(index + 1)
  const back = () => go(index - 1)

  /** Damped past the ends: it moves, so it answers, but it does not go far. */
  const rubber = (dx: number): number => {
    const blocked = dx > 0 ? index === 0 : hold != null || isLast
    return blocked ? dx * 0.28 : dx
  }

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    gesture.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      lastX: event.clientX,
      at: event.timeStamp,
      speed: 0,
      axis: 'undecided',
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gesture.current
    if (current === null || current.id !== event.pointerId) return
    const dx = event.clientX - current.x
    const dy = event.clientY - current.y

    if (current.axis === 'undecided') {
      if (Math.abs(dx) < DRAG_START_PX && Math.abs(dy) < DRAG_START_PX) return
      // A slide can be taller than the screen — the question hub with three
      // answers open is — so a vertical drag belongs to the scroller and this
      // gesture ends rather than fighting it.
      current.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      if (current.axis === 'y') {
        gesture.current = null
        return
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    const elapsed = event.timeStamp - current.at
    if (elapsed > 0) current.speed = Math.abs(event.clientX - current.lastX) / elapsed
    current.lastX = event.clientX
    current.at = event.timeStamp
    setDrag(rubber(dx))
  }

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gesture.current
    if (current === null || current.id !== event.pointerId) return
    gesture.current = null
    const dx = event.clientX - current.x
    const travelled = Math.abs(dx)
    setDrag(0)

    if (current.axis === 'x') {
      const thrown = travelled > FLICK_PX && current.speed > FLICK_SPEED
      if (travelled > COMMIT_PX || thrown) {
        if (dx < 0) forward()
        else back()
      }
      return
    }

    if (travelled > TAP_PX) return
    // The affordances inside a slide answer their own taps; a question card
    // that also turned the page would be unusable.
    if (event.target instanceof Element && event.target.closest('button, a, input') !== null) return
    const width = event.currentTarget.clientWidth
    if (index > 0 && event.clientX < width * BACK_ZONE) back()
    else forward()
  }

  const onPointerCancel = () => {
    gesture.current = null
    setDrag(0)
  }

  return (
    <div
      data-stage
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-[56] overflow-hidden"
    >
      <Aurora hue={hue} />

      {/* Where you are, and how much is left. Unskippable or not, nobody
          should have to guess how long this goes on. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 z-20 flex gap-1 px-4 pt-[max(0.75rem,env(safe-area-inset-top))]"
      >
        {segments.map((i) => (
          <span
            key={i}
            className={cn(
              'h-[3px] flex-1 rounded-full transition-all duration-300',
              i < index && 'bg-[var(--stage-ink)]/45',
              i === index && 'bg-[var(--stage-figure)] shadow-[0_0_10px_var(--stage-figure)]',
              i > index && 'bg-[var(--stage-line)]',
            )}
          />
        ))}
      </div>

      {/* The band under the rail: a pulse that says something is in the room,
          her name once she has said it, and what this sequence is on the
          right. Story UIs have always put the account and the timestamp up
          here, and without it the top third of a slide is dead space. */}
      <div
        aria-hidden="true"
        style={{ top: 'max(1.6rem, calc(env(safe-area-inset-top) + 1rem))' }}
        className={cn(
          'absolute inset-x-0 z-20 flex h-9 items-center justify-between gap-3 px-5',
          'text-[10px] uppercase tracking-[0.22em]',
          onClose != null && 'pr-14',
        )}
      >
        <span className="flex items-center gap-2 text-[var(--stage-dim)]">
          <span className="stage-breathe block size-1.5 shrink-0 rounded-full bg-[var(--stage-glow)] shadow-[0_0_10px_var(--stage-glow)]" />
          {crest}
        </span>
        {note != null && <span className="text-[var(--stage-mute)]">{note}</span>}
      </div>

      {onClose != null && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Uždaryti"
          className="absolute top-[max(1.6rem,calc(env(safe-area-inset-top)+1rem))] right-3 z-30 grid size-9 place-items-center rounded-full text-[var(--stage-mute)] transition-colors hover:text-[var(--stage-ink)]"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      {/* biome-ignore lint/a11y/noStaticElementInteractions: the whole surface
          is the control — swipe, tap and arrow keys all reach the same three
          moves, and the labelled chevron below carries the accessible name for
          anything that cannot make a gesture. Giving this div a widget role
          would be a lie: it is a page, not a slider. */}
      <div
        ref={surface}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' || event.key === 'PageDown') forward()
          else if (event.key === 'ArrowLeft' || event.key === 'PageUp') back()
          else return
          event.preventDefault()
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{ touchAction: 'pan-y' }}
        className="relative z-10 flex h-full flex-col outline-none"
      >
        <div
          style={{ transform: drag === 0 ? undefined : `translate3d(${drag}px,0,0)` }}
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            drag === 0 && 'transition-transform duration-200 ease-[var(--ease-out-quick)]',
          )}
        >
          <div
            key={index}
            data-dir={dir}
            aria-live="polite"
            className="stage-slide flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pt-[max(4.4rem,calc(env(safe-area-inset-top)+3.8rem))] pb-4"
          >
            {children}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-center gap-2 px-6 pt-2 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          {hold != null ? (
            <p className="text-center text-[var(--stage-mute)] text-sm">{hold}</p>
          ) : isLast ? (
            <button
              type="button"
              onClick={finish}
              className="rounded-full border border-[var(--stage-figure)]/45 bg-[var(--stage-figure)]/10 px-7 py-2.5 font-display font-semibold text-[var(--stage-figure)] text-base shadow-[0_0_28px_-6px_var(--stage-figure)] transition-colors active:bg-[var(--stage-figure)]/20"
            >
              {finishLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={forward}
              aria-label="Toliau"
              className="grid size-11 place-items-center rounded-full border border-[var(--stage-line)] text-[var(--stage-ink)]"
            >
              <span className="stage-breathe grid size-11 place-items-center rounded-full">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
                  <path
                    d="M9 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          )}

          {index === 0 && hold == null && (
            <p
              aria-hidden="true"
              className="stage-hint text-[11px] text-[var(--stage-mute)] uppercase tracking-[0.14em]"
            >
              Brauk
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
